import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import AddCirclePlusButton from 'common/display/Profile/components/AddCirclePlusButton';
import { CurrentProviderContextProvider } from 'src/common/contexts/CurrentProviderContext';
import FeatureFlagContainer from 'src/common/utils/FeatureFlags/FeatureFlagContainer';
import { CREATE_FEE_SCHEDULE_PROVIDED_SERVICE } from 'src/components/Cases/constants';
import hideInteractions from 'src/components/Facesheet/Cases/utils/hideElement';
import { isEmpty, get, flow } from 'lodash';
import { isCaseOpen } from 'src/components/Cases/utils';
import { Spinner } from 'common/spinners';
import ProvidedServiceCards from './cards/ProvidedServiceCards';
import NoProvidedServiceCard from './cards/NoProvidedServiceCard';
import ProvidedServiceFormWrapper from './form/ProvidedServiceFormWrapper';
import { useFindProgram, useFindProvidedServices } from '../../../hooks';

function ProvidedServices({
  caseId,
  contactId,
  editOnFirstRender,
  editProvidedServiceId,
  groupId,
  isCoordinationGroup,
  serviceAuthorization,
  serviceCase,
}) {
  const programId = get(serviceCase, 'program.id');
  const { program, isLoading: isProgramLoading } = useFindProgram(programId);
  const feeScheduleProgram = program?.fee_schedule_program;
  const paymentType = feeScheduleProgram?.payment_type;

  const { providedServices, isLoading: isPSLoading } = useFindProvidedServices(caseId);

  const [isFeeScheduleClicked, setClick] = useState(false);
  const [isEditing, setEditMode] = useState(editOnFirstRender);
  const [editedProvidedServiceId, setProvidedServiceEditId] = useState(editProvidedServiceId);
  const authStatus = serviceAuthorization.state;
  const hasNonApprovedAuth = !!authStatus && (authStatus !== 'approved');

  const canAddServices = isCaseOpen(serviceCase);
  const showServicesProvidedInteractions = !hideInteractions({ isCoordinationGroup, serviceCase, groupId });

  const createAddFeeScheduleServicesHandler = useCallback(() => {
    setClick(!isFeeScheduleClicked);
  }, [isFeeScheduleClicked]);

  const updateProvidedServicesHandler = useCallback((providedServiceIdSelected) => {
    setEditMode(providedServiceIdSelected !== '');
    setProvidedServiceEditId(providedServiceIdSelected);
  }, []);

  const authToolTip =
    'You can only add a Contracted Service to the case if the authorization request is accepted.';
  const isLoading = isProgramLoading || isPSLoading;

  return (
    <div className="mt-6">
      <CurrentProviderContextProvider providerId={groupId}>
        <div className="flex justify-between items-center mb-4">
          <h2>Contracted Service</h2>
          <div>
            {
              canAddServices && showServicesProvidedInteractions ? (
                <AddCirclePlusButton
                  addHandler={createAddFeeScheduleServicesHandler}
                  id="add-fee-schedule-service-provided-button"
                  labelText="Add New Contracted Service"
                  disabled={isFeeScheduleClicked || isEditing || hasNonApprovedAuth}
                  toolTip={hasNonApprovedAuth ? authToolTip : ''}
                />
              ) : null
            }
          </div>
        </div>
        {
          !isFeeScheduleClicked && isLoading && (<Spinner />)
        }
        {
          !isFeeScheduleClicked && !isLoading && (
            (isEmpty(providedServices) ? (<NoProvidedServiceCard />) :
              (
                <ProvidedServiceCards
                  contactId={contactId}
                  providedServices={providedServices}
                  editMode={updateProvidedServicesHandler}
                  editOnFirstRender={editOnFirstRender}
                  isEditing={isEditing}
                  serviceCase={serviceCase}
                  program={program}
                  feeScheduleProgram={feeScheduleProgram}
                  selectedProvidedServiceId={editedProvidedServiceId}
                  showServicesProvidedInteractions={showServicesProvidedInteractions}
                />
              )))
        }
        {
          isFeeScheduleClicked && (
            <ProvidedServiceFormWrapper
              contactId={contactId}
              serviceCase={serviceCase}
              isBillable={get(feeScheduleProgram, 'billable')}
              cancelFunction={() => setClick(!isFeeScheduleClicked)}
              formName={CREATE_FEE_SCHEDULE_PROVIDED_SERVICE}
              paymentType={paymentType}
            />
          )
        }
      </CurrentProviderContextProvider>
    </div>
  );
}

ProvidedServices.propTypes = {
  caseId: PropTypes.string,
  contactId: PropTypes.string.isRequired,
  editProvidedServiceId: PropTypes.string,
  editOnFirstRender: PropTypes.bool,
  groupId: PropTypes.string.isRequired,
  isCoordinationGroup: PropTypes.bool.isRequired,
  serviceAuthorization: PropTypes.object,
  serviceCase: PropTypes.shape({
    id: PropTypes.string,
    contact: PropTypes.object,
  }).isRequired,
};

ProvidedServices.defaultProps = {
  caseId: '',
  editProvidedServiceId: '',
  editOnFirstRender: false,
  serviceAuthorization: {},
};

export default flow(
  FeatureFlagContainer,
)(ProvidedServices);
